<template>
  <ValidationObserver
    v-slot="{ handleSubmit }"
    ref="editModal"
  >
    <b-modal
      id="dmCreateEditModal"
      v-model="modal.show"
      class="modal"
      size="xl"
      scrollable
      :title="getTitle"
      :ok-disabled="isLoaded || isClicked || loading || validationLoading"
      :cancel-disabled="isLoaded"
      @ok.prevent="handleSubmit(onSubmit)"
      @show="onShow"
      @hidden="hideModal"
    >
      <content-spinner
        :show="isLoaded || loading"
        :fullscreen="true"
      />
      <show-error
        :show="showError"
        :text="modalError"
      />
      <div>
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <template v-for="(field, index) in currentFields">
            <lookup-input-component
              v-if="field.dataTypeName === 'lookup'"
              transit-ref="lkp-multiselect"
              :is-label-visible="true"
              :is-todo-list="true"
              :field="field"
              :props-value="modal.data[field.name]"
              :index="index"
              :is-edit-row-modal="true"
              :disabled="field.disabled || field.currentUserPermissionLevel === 'read' || isCalculatedLookup(field)"
              @on-select="onLookupChange"
              @on-add-new-relation="onAddNewRelationalEntity"
            />
            <input-widget
              v-else
              :id="`addEditRowModalField_${field.name}`"
              :ref="field.fieldGuid"
              :model="form"
              :field="field"
              :rls="field.isRLSField"
              :date-format="getDateFormat(field)"
              :autofocus="field.autofocus"
              :error="errors[field.name]"
              :skip-validation-rules="isBulkEditModal"
              :disabled="field.disabled || field.currentUserPermissionLevel === 'read'"
              @blur="onInputChange"
              @change="onFieldChange($event, field)"
              @select="onInputChange"
            />
          </template>
          <!--        This button MUST NOT be deleted-->
          <button
            type="submit"
            class="d-none"
          />
        </b-form>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import InputWidget from './components/InputWidget';
import ContentSpinner from '../ContentSpinner';
import moment from 'moment';
import ShowError from '../ShowError';
import {getDataTypeJson} from '@/core/utils/getDataTypeJson';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';
import {axiosClient} from '@/core/services/http.service';


const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
export default {
  name: 'EditRowModal',
  components: {
    ShowError,
    ContentSpinner,
    InputWidget,
    LookupInputComponent,
  },
  props: {
    modalKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {},
      validationRequest: {
        queue: [],
        running: false,
        payload: {},
      },
      currentFields: [],
      errors: {},
      inlineLoaded: false,
      validationLoading: false,
      latestUpdateDate: '',
      validationErrors: false,
      defaultLookupValue: {},
      showError: false,
      modalError: '',
      isClicked: false,
      loading: false,
    };
  },
  watch: {
    ['modal.data']() {
      this.form = {...this.modal.data};
      this.formFields();
    },
  },
  computed: {
    ...mapGetters(['fields']),
    ...mapState({
      relationalModals: state => state.table.relationalModals,
      schema: state => state.table.schema,
      fieldValues: state => state.fieldValues,
      permissionTypes: state => state.permissionTypes,
      table: state => state.table,
      tableData: state => state.tableData.data,
      validation: state => state.validation,
    }),
    modal() {
      return this.relationalModals[this.modalKey];
    },
    getTitle() {
      if (this.isBulkEditModal) {
        return 'Bulk Update';
      }
      return `${(this.modal.data && Object.keys(this.modal.data).length > 0) || this.modal.todo ? 'Edit The' : 'Add New'} Record`;
    },
    isLoaded() {
      return this.modal.inline ? this.modal.loading && !this.inlineLoaded : this.table.loading;
    },
    isValidatedRow() {
      return this.form[this.validation.status] === this.validation.validated;
    },
    filtersApplied() {
      return this.modal.filters && Object.keys(this.modal.filters).some(fieldType => this.modal.filters[fieldType].length);
    },
    isBulkEditModal() {
      return this.modal && this.modal.isBulkEditModal;
    },
  },
  methods: {
    ...mapActions([
      'addNewRecord',
      'hideAddNewRecordModal',
      'getTable',
      'getPopulatedRecord',
      'showAddNewRecordModal',
      'updateRelationLookupData',
      'unsetUncheckedItem',
      'updateTableItems',
      'bulkUpdateTableRecords',
      'updateLookupOptions',
    ]),
    beforeHide(e) {
      if (this.modal.loading) {
        e.preventDefault();
        return false;
      }
    },
    getDateFormat(field) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      const isDateField = field.dataTypeName === this.fieldValues.date;
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${isDateField ? '' : ' HH:mm:ss'}`;
    },
    formFields() {
      const schemaCopy = JSON.parse(JSON.stringify(this.schema));//making copy to make sure that schema is not changed
      const currentFields = (this.modal.inline || this.modal.todo) && this.modal.fields
        ? this.modal.fields
        : schemaCopy.fields;

      this.currentFields = currentFields
        .filter((elem) => {
          const jsonData = elem.dataTypeJSON ? JSON.parse(elem.dataTypeJSON) : {};

          // if (elem.isPrimaryKey && !this.form[elem.name]) {
          //   this.form[elem.name] = uuidv4();
          // }
          if (this.modal.inline && Object.keys(this.defaultLookupValue).length > 0 && !this.form[elem.name]) {
            this.form[elem.name] = this.defaultLookupValue[elem.name];
          }
          if (jsonData.DefaultValue && !this.form[elem.name]) {
            this.form[elem.name] = jsonData.DefaultValue;
          }
          if (elem.dataTypeName === 'lastUpdatedDate') {
            const primaryKeyName = this.table.schema.fields.find(f => f.isPrimaryKey).name;
            const currentRow = this.table.data.find(t => t[primaryKeyName] === this.form[primaryKeyName]);
            if (currentRow) {
              this.latestUpdateDate = currentRow[elem.name];
            }
          }
          return !elem.isInternal;
        })
      // .filter((elem) => {
      //   return !['lastUpdatedDate','createdDate','lastUpdatedBy'].includes(elem.dataTypeName);
      // })
      // .filter(f => (f.displayInForm && !(f.originalType === this.fieldValues.uuid && !this.form[this.fieldValues.uuid])))
        .filter(f => (f.displayInForm))
        .map((elem) => {
          //Needs to rerender view for hierarchy list. Skip for lookups
          // if (elem.dataTypeName !== this.fieldValues.lookup || elem.dataTypeName !== this.fieldValues.parentChildHierarchy) {
          //   this.form[elem.name] = this.form[elem.name] ? this.form[elem.name] : null
          // }
          if (this.form[elem.name] === undefined) {

            // console.log(elem.name, 'elem.name');
            this.form[elem.name] = null;
          }
          const jsonData = getDataTypeJson(elem.dataTypeJSON);

          elem.label = elem.displayName;
          if (this.isPostedStatus(elem)) {
            elem.type = 'select';
            if (!this.form[elem.name]) {
              this.form[elem.name] = 'Pending';
            }
            elem.disabled = ![
              this.permissionTypes.admin,
              this.permissionTypes.edit,
              this.permissionTypes.unpost,
              this.permissionTypes.post,
            ].includes(this.schema.currentUserPermissionLevel);

          } else if (elem.dataTypeName === this.fieldValues.text || elem.isPrimaryKey) {
            if (jsonData.UsePredictiveSearch) {
              elem.type = 'search-input';
              elem.suggestions = elem.suggestions[elem.name];
              elem.hint = 'Please enter 3 or more characters for search';
            } else {
              if (jsonData.IsLineBreak) {
                elem.type = 'textarea';
              } else {
                elem.type = 'text';
              }
            }
          } else if (
            elem.dataTypeName === this.fieldValues.decimalNumber
              || elem.dataTypeName === this.fieldValues.floatNumber
              || elem.dataTypeName === this.fieldValues.wholeNumber
          ) {
            elem.type = 'number';
            if (jsonData.IsPercentage) {
              elem.placeholder = `${elem.label}%`;
            }
          } else if (elem.dataTypeName === this.fieldValues.dateTime) {
            elem.type = 'datetime';
            elem.placeholder = this.getDateFormat(elem);
          } else if (elem.dataTypeName === this.fieldValues.date) {
            elem.type = 'date';
            elem.placeholder = this.getDateFormat(elem);
          } else if (elem.dataTypeName === this.fieldValues.lookup) {
            elem.type = 'multi-select';
            elem.isTable = true;
            if (Object.keys(this.modal).length > 0) {
              const item = this.modal.data;
              const suggestName = item[`${elem.name}__$LookupStatus`];

              // console.log(item, 'item');
              // console.log(suggestName, 'suggestName');
              if (suggestName) {
                const suggestions = suggestName.Suggestions || suggestName.suggestions;
                if (suggestions && suggestions.length > 0) {
                  suggestName.options = [];
                  suggestName.options.push({
                    id: 'other',
                    text: 'Other Values',
                    $isDisabled: true,
                  });
                  suggestions.sort(function (a, b) {
                    const aValue = a.Confidence || a.confidence;
                    const bValue = b.Confidence || b.confidence;
                    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                  });
                  suggestName.options = [...suggestName.options, ...elem.data.options];
                  suggestions.forEach(suggestItem => {
                    const recordGuid = suggestItem.RecordGuid || suggestItem.recordGuid;
                    const suggestedValue = suggestItem.SuggestedValue || suggestItem.suggestedValue;
                    const percentage = suggestItem.Confidence || suggestItem.confidence;
                    const index = suggestName.options.findIndex(data => data.id === recordGuid);
                    if (index > -1) {
                      suggestName.options.splice(index, 1);
                    }
                    suggestName.options.unshift({
                      id: recordGuid,
                      text: suggestedValue,
                      backgroundColor: this.computedColor(percentage),
                    });
                  });
                  suggestName.options.unshift({
                    id: 'suggested',
                    text: 'Suggested Values',
                    $isDisabled: true,
                  });
                }
              }
            }
          } else if (elem.dataTypeName === this.fieldValues.boolean) {
            elem.type = 'checkbox';
          } else if (elem.dataTypeName === this.fieldValues.parentChildHierarchy) {
            const primaryField = elem.data.options.hierarchyField.primary;
            const displayField = elem.data.options.hierarchyField.display;

            elem = {
              ...elem,
              ...this.buildTree(elem.data.options.items, elem),
            };

            elem.data.options = {
              ...elem.data.options,
              data: elem.tree,
              levels: elem.treeLevels,
              optionByLevel: {},
            };

            elem.config = {
              optionLabel: displayField.name,
              optionValue: primaryField.name,
            };


            if (jsonData.ControlType === 'select' || jsonData.ControlType === 'list') {
              if (this.form[elem.name]) {
                // return ;
                this.form[elem.name] = this.setParentNodes(elem, this.form[elem.name]);
              }
              const levels = elem.data.options.levels;
              if (!this.form[elem.name]) {
                this.form[elem.name] = {};
                for (const level in levels) {
                  this.form[elem.name] = {
                    ...this.form[elem.name],
                    [level]: null,
                  };
                  elem.data.options.optionByLevel = {
                    ...elem.data.options.optionByLevel,
                    [level]: [],
                  };
                  if (level === 0) {
                    elem.data.options.optionByLevel[0] = elem.data.options.data;
                  }
                }
              }
            }

            if (jsonData.ControlType === 'list') {
              elem.type = 'hierarchy-list';
            } else if (jsonData.ControlType === 'select') {
              elem.type = 'hierarchy-select';
            } else {
              elem.type = 'hierarchy-tree';
            }
          }

          if (elem.isCalculated) {
            elem.disabled = true;
          }

          if ([this.fieldValues.date, this.fieldValues.dateTime].includes(elem.dataTypeName) && this.form[elem.name]) {
            this.form[elem.name] = moment(this.form[elem.name]).format(this.getDateFormat(elem));
          }

          if (elem.dataTypeJSON) {
            const jsonData = JSON.parse(elem.dataTypeJSON);
            if (jsonData) {
              if (
                [this.fieldValues.date, this.fieldValues.dateTime].includes(elem.dataTypeName)
                  && jsonData['DefaultDateNow']
              ) {
                this.form[elem.name] = moment(this.form[elem.name]).format(this.getDateFormat(elem));
                this.form = {...this.form};
              }
              if (
                elem.dataTypeName === this.fieldValues.boolean
                  && jsonData['DefaultOnInsert']
                  && [null, undefined].includes(this.form[elem.name])
              ) {
                this.form[elem.name] = true;
              }
            }
          }

          if (
            elem.dataTypeName === this.fieldValues.boolean
              && (this.form[elem.name] === undefined || this.form[elem.name] === null)
          ) {
            this.form[elem.name] = false;
          }

          if (elem.dataTypeName === this.fieldValues.lookup) {
            if (this.form[elem.name]) {
              if (Object.keys(this.form[elem.name]).length > 0 && this.form[elem.name].id) {
                this.form[elem.name] = this.form[elem.name].id;
              }
              // this.form[elem.name] = elem.data.options.find(e => e.id === this.form[elem.name]);
              // console.log(' this.form[elem.name])))))))',  this.form[elem.name]);

            }
            if (
              Object.keys(this.modal).length > 0
                && this.modal.defaultLookupValue
                && this.modal.defaultLookupValue[elem.name] !== undefined
                && !this.modal.inline
            ) {
              this.form[elem.name] = {
                id: null,
                text: this.modal.defaultLookupValue[elem.name],
              };
            }
          }

          if (elem.dataTypeName === 'lastUpdatedDate') {
            const primaryKeyName = this.table.schema.fields.find(f => f.isPrimaryKey).name;
            const currentRow = this.table.data.find(t => t[primaryKeyName] === this.form[primaryKeyName]);
            if (currentRow) {
              this.latestUpdateDate = currentRow[elem.name];
            }
          }
          this.form = {...this.form};
          return elem;
        });
      if (this.currentFields && this.currentFields.length > 0) {
        this.currentFields.map(field => {
          if (!this.isPostedStatus(field)) {
            field.disabled = this.hasPostedValue(this.form, currentFields);
          }
          if (this.isBulkEditModal && field.dataTypeName === this.fieldValues.boolean) {
            field.disabled = true;
            field.loopCheckbox = false;
            field.class = 'bulk-update-modal-checkbox';
          }
          return field;
        });
        this.currentFields[0]['autofocus'] = true;
      }
      return this.currentFields;
    },
    async onFieldChange(newValue, field) {
      await this.onInputChange();
      if (this.isBulkEditModal) {
        this.showError = false;
        this.modalError = '';
        // Check if user is updating business keys and more than one record is selected we need to show error to the user
        const businessKeyFields = this.currentFields.filter(f => f.isBusinessKey);

        // Check if all business key fields are populated
        let allFilled = true;
        for (const f of businessKeyFields) {
          if (!this.form[f.name]) {
            allFilled = false;
            break;
          }
        }

        // If all business keys are filled and there are more than one row selected to edit we need to show an error to the user that we have duplicate business key
        if (allFilled && !!businessKeyFields.length) {
          this.showError = true;
          this.modalError = `Duplicate business key of ${businessKeyFields.map(f => f.name).join('||')}`;
          return true;
        }
        this.currentFields = this.currentFields.map(f => {
          if ((field.dataTypeName === this.fieldValues.boolean) && (field.fieldGuid === f.fieldGuid)) {
            if (newValue && !field.loopCheckbox) {
              f.loopCheckbox = true;
              this.form[f.name] = true;
            } else if (newValue && field.loopCheckbox) {
              f.disabled = true;
              f.loopCheckbox = false;
              this.form[f.name] = false;
            } else {
              this.form[f.name] = newValue;
            }
          }
          return f;
        });
        this.form = {...this.form};
      }
    },
    formatPostData(item) {
      const fieldMap = {};
      const currentFields = this.modal.inline ? this.modal.fields : this.table.schema.fields;

      //Since we get data by fields name and API expects displayName convert params
      currentFields.forEach((field) => {
        let primaryField;
        if (field.dataTypeName === this.fieldValues.parentChildHierarchy) {
          primaryField = field.data.options.hierarchyField.primary;
        }
        const json = JSON.parse(field.dataTypeJSON);
        fieldMap[field.name] = {
          name: field.name,
          isInternal: field.isInternal,
          isPrimaryKey: field.isPrimaryKey,
          dataTypeName: field.dataTypeName,
          valueKey: primaryField ? primaryField.name : null,
          controlType: json.ControlType,
        };
      });

      const params = {};
      for (const name in item) {
        const field = fieldMap[name];
        if (field === undefined) {
          delete item[name];
          continue;
        } else if (field && field.isInternal && !field.isPrimaryKey) { //Skip auto populated columns since it throws error if it's passed
          continue;
        }

        let val = item[name];

        if (field && field.dataTypeName === this.fieldValues.lookup) {
          // val = val ? val.id : null;

          val = val ? val : null;
        }

        if (field && field.dataTypeName === this.fieldValues.parentChildHierarchy && field.controlType !== 'tree') {
          val = null;
          for (const level in item[name]) {
            if (item[name][level]) {
              if (field.controlType === 'list') {
                val = item[name][level];
              } else if (field.controlType === 'select') {
                val = item[name][level][field.valueKey];
              }
            }
          }
        }
        params[name] = val;
      }
      return params;
    },
    isPostedStatus(field) {
      return this.schema.postedStatusGuid === field.fieldGuid;
    },
    isLineBreakTextField(field) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      return jsonData.IsLineBreak;
    },
    hasPostedValue(item, fields) {
      const postedStatusField = fields.find(field => this.isPostedStatus(field));
      return postedStatusField ? item[postedStatusField.name] === 'Posted' : false;
    },
    isValid(postData) {
      this.errors = {};

      this.currentFields.forEach((field) => {
        if (field.dataTypeName === this.fieldValues.parentChildHierarchy) {
          const json = JSON.parse(field.dataTypeJSON);
          const fieldToDisplay = json.FieldToDisplayGuid;
          const parentIsSibling = this.currentFields.find((f) => f.fieldGuid === fieldToDisplay);  //Check if hierarchy's parent is from same table.
          //Validate Hierarchy
          if (parentIsSibling) {
            const levelField = this.currentFields.find((f) => f.fieldGuid === json.LevelFieldGuid);
            const levelName = this.form[levelField.name];
            let currentLevelFound = false;
            let isAllPopulated = true;

            let compareVal = {};
            if (json.ControlType === 'list' || json.ControlType === 'select') {
              compareVal = this.form[field.name];
            } else if (json.ControlType === 'tree') {
              compareVal = this.getParentNodes(this.form[field.name], field.data.options.data, field);
            }


            for (const level in field.treeLevels) {
              if (!compareVal || !compareVal[level]) {
                isAllPopulated = false;
              }

              if (levelName === field.treeLevels[level].levelName) {
                currentLevelFound = true;
              }

              if (!currentLevelFound && (!compareVal || !compareVal[level])) {
                this.errors[field.name] = `Fields before ${levelName} must be set`;
                return false;
              }

              if (currentLevelFound && compareVal && compareVal[level]) {
                this.errors[field.name] = `Fields after ${levelName} must not be set`;
                return false;
              }
            }
          }
        }
      });
      return Object.keys(this.errors).length === 0;
    },
    isFieldBoolean(field) {
      return field.dataTypeName === this.fieldValues.boolean;
    },
    async onSubmit(isConfirmed = false) {
      this.loading = true;
      const item = {...this.form};
      let tableId;
      let primaryKey;

      if (this.modal && this.modal.fields) {
        const isPrimaryKeyExistInFields = this.modal.fields.find(f => f.isPrimaryKey).name;
        const isPrimaryKeyExistInSchema = this.table.schema.fields.find(f => f.isPrimaryKey).name;
        primaryKey = isPrimaryKeyExistInFields || isPrimaryKeyExistInSchema;
      }

      this.isClicked = true;

      if (this.modal.inline) {
        tableId = this.modal.relationalTableId;
      }

      const postData = this.formatPostData(this.form);
      if (!this.isValid(postData)) {
        this.isClicked = false;
        this.loading = false;
        return;
      }

      this.currentFields.forEach(field => {
        tableId = field.entityGuid;
        if (
          !field.isInternal
            && (this.form[field.name] === ''
                || this.form[field.name] === undefined
                || this.form[field.name] === 'Invalid date'
            )
        ) {
          postData[field.name] = null;
        }
        if (field.dataTypeName === this.fieldValues.boolean && postData[field.name] === null) {
          postData[field.name] = false;
        }
        // if ([this.fieldValues.wholeNumber, this.fieldValues.decimalNumber].includes(field.dataTypeName) && postData[field.name] === null) {
        //   postData[field.name] = 0
        // }
        if (field.dataTypeName === this.fieldValues.lookup) {
          const lookupName = `${field.name}__$LookupStatus`;
          if (item[lookupName]) {
            if (item[lookupName].options) {
              // console.log(this.modal.data[lookupName].options, 'this.modal.data[lookupName].options');
              delete item[lookupName].options;
            }
            if (
              item[field.name]
                && item[field.name].id === null
                && item[lookupName].Suggestions
            ) {
              item[lookupName].Suggestions = [];
            }
            postData[lookupName] = item[lookupName];
            // postData[lookupName] = this.modal.data[lookupName];
          }
        }
        if (
          [
            this.fieldValues.dateTime,
            this.fieldValues.date,
          ].includes(field.dataTypeName) && item[field.name]
        ) {
          const dateFormat = this.getDateFormat(field);
          const defaultFormat = 'YYYY/MM/DD';

          postData[field.name] = this.fieldValues.date === field.dataTypeName ?
            this.$options.filters.toDate(moment(item[field.name], dateFormat).toDate(), defaultFormat) :
            this.$options.filters.toDatetime(moment(item[field.name], dateFormat).toDate(), `${defaultFormat} HH:mm:ss`);

          if (postData[field.name] === 'Invalid date') {
            postData[field.name] = null;
          }
        }
        if (field.dataTypeName === this.fieldValues.text && item[field.name]) {
          const itemValue = item[field.name].trim();
          postData[field.name] = this.isLineBreakTextField(field) ? itemValue : itemValue.replace(/\s\s+/g, ' ');
        }
      });

      postData['entityGuid'] = tableId;
      // If field is hidden make this field value null
      const schemaCopy = JSON.parse(JSON.stringify(this.schema));
      const currentFields = this.modal.inline
        ? this.modal.fields
        : schemaCopy.fields;

      currentFields.filter(f => !f.displayInForm).forEach(f => {
        const jsonData = JSON.parse(f.dataTypeJSON);

        if (jsonData.DefaultValue && !postData[f.name]) {
          return postData[f.name] = jsonData.DefaultValue;
        } else if (postData[f.name] === undefined) {
          return postData[f.name] = null;
        }
      });
      
      // Strip out non-writeable fields
      const fieldPermissions = {};
      Object.values(this.currentFields).forEach(f => {
        fieldPermissions[f.name] = f.currentUserPermissionLevel;
      });
      Object.keys(postData).forEach(k => {
        if (['read', 'none', 'deny'].includes(fieldPermissions[k])) {
          delete postData[k];
        }
      });
     
      const params = {
        data: [postData],
        tableId: tableId,
        modalKey: this.modalKey,
        primaryKey,
      };

      if (this.isBulkEditModal) {
        const confirmed = await this.$confirm(
          'Bulk Update Rows',
          'Would you like to update value of selected rows?',
        );
        if (confirmed) {
          params.data = JSON.parse(JSON.stringify(this.table.checkedItems)).map(item => {
            this.currentFields.forEach(field => {
              if (field.isPrimaryKey) return false;
              if (field.dataTypeName === this.fieldValues.boolean && field.disabled) return false;

              const newItem = params.data.find(p => p[field.name] || p[field.name] === false);
              if (newItem !== undefined && newItem[field.name] !== null) {
                item[field.name] = newItem[field.name].id || newItem[field.name];
              }
            });
            // Strip out non-writeable fields
            Object.keys(item).forEach(k => {
              if (['read', 'none', 'deny'].includes(fieldPermissions[k])) {
                delete item[k];
              }
            });
            return item;
          });
        } else {
          this.loading = this.isClicked = false;
          return false;
        }
      }

      if (this.latestUpdateDate) {
        postData['UploadAsAtDate'] = this.latestUpdateDate;
      }
      const validate = this.validation;
      if (isConfirmed) {
        postData[validate.fieldName] = validate.confirmed;
      } else if (item[validate.status] === validate.validated) {
        const success = await this.$confirm(
          'Confirmed Row',
          'Would you like to change \'Validated\' row to \'Confirmed\'?',
        );
        if (success) {
          postData[validate.fieldName] = validate.confirmed;
        }
      }

      let response;
      if (
        (this.table.data.length && this.isBulkEditModal)
          && ((this.table.data.length === this.table.checkedItems.length)
              || (this.filtersApplied && !this.table.checkedItems.length))
      ) {
        const data = this.currentFields.reduce((newItem, field) => {
          const fieldName = field.name;
          if ((
            field.isPrimaryKey
                  || field.isInternal
                  || (this.isFieldBoolean(field) && !field.loopCheckbox))
              || [null, undefined].includes(postData[fieldName])
          ) {

            return newItem;
          };

          newItem[fieldName] = postData[fieldName].id || postData[fieldName];

          return newItem;
        }, {});
        response = await this.bulkUpdateTableRecords({
          data,
          filters: this.modal.filters,
          entityId: this.$route.params.tableId,
        });
      } else {
        response = await this.addNewRecord(params);
      }

      const {success, body, updatedRecord} = response;
      this.loading = false;
      if (success) {
        if (this.isBulkEditModal) {
          this.updateTableItems({
            updatedItems: params.data,
            primaryKey,
          });
        }
        const resData = body.ResultSetTemp;
        if (resData.length > 0) {
          const updatedData = resData[0];
          if (updatedData['uploadStatus'] === 'rejected') {
            this.showErrorContent(updatedData['validationErrors']);
          } else {
            const params = {
              ...this.modal,
              updatedId: body.ResultSetTemp[0].recordGuid,// id of lkp option that was created
              insertData: {
                ...body,
                updatedRecord,
              },
            };
            if (
              Object.keys(this.relationalModals).length === 1
                && (!isConfirmed || (isConfirmed && !params.inline))
            ) {
              this.$emit('changed-row', params);
            }

            if (
              !this.isBulkEditModal
                && ((!item[validate.status]
                        || item[validate.status] === validate.validationFailed
                        || item[validate.status] === validate.confirmed
                )
                    && updatedRecord[validate.status] === validate.validated)
            ) {
              this.form = {...updatedRecord};
              this.formFields();
              const success = await this.$confirm(
                'Confirmed Row',
                'Would you like to change \'Validated\' row to \'Confirmed\'?',
              );
              if (success) {
                await this.onSubmit(true);
              }
            }

            if (Object.keys(this.relationalModals).length > 1) {
              await this.onChangedRelationalRow(params);
            }
            this.$toasted.success(
              `Record${this.modal && this.modal.isBulkEditModal ? 's' : ''} saved successfully`,
              {duration: TOASTER_DURATION},
            );
            this.defaultLookupValue = {};
            if (this.table.checkedItems.length && this.isBulkEditModal) {
              await this.unsetUncheckedItem({type: 'all'});
            }
            this.hideModal();
          }
        } else if (body.errors.length > 0) {
          this.showErrorContent(body.errors);
        } else {
          this.hideModal();
        }
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
        this.hideModal();
      }
      this.$emit('is-bulk-update-happened', true);
    },
    showErrorContent(errors) {
      this.isClicked = false;
      this.showError = true;
      this.modalError = errors.map(error => {
        let parsedError;

        try {
          parsedError = JSON.parse(error);
        } catch (err) {
          parsedError = error;
        }

        if (typeof parsedError === 'object') {
          return parsedError.Message;
        } else {
          return parsedError;
        }
      }).join(' ');
      // this.modal.data = {...this.modal.data}
    },
    async onAddNewRelationalEntity(item, index, field) {
      const fields = this.modal.fields || this.fields;
      const primary = fields.find((f) => f.isPrimaryKey);
      const params = {
        inline: true,
        record: {},
        todo: false,
        relationalTableId: field.relationalTableId,
        field: field,
        parentKey: this.modalKey,
        parentData: {...this.form},
        recordUuid: this.form[primary.name],
        entityUuid: field.entityGuid,
        defaultLookupValue: this.getLookupDefaultValue(this.form, field, true),
      };
      this.showAddNewRecordModal(params);
    },
    getLookupDefaultValue(item, field, isRelational = false) {
      if (field !== undefined) {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        let lookupDefaultValue;
        const relationalField = field.data.lookup;
        if (item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          lookupDefaultValue = suggestions[0].OriginalValue || suggestions[0].originalValue;
        }
        return isRelational ? {[relationalField.value]: lookupDefaultValue} : {[field.name]: lookupDefaultValue};
      }
    },
    isValidSuggestions(suggestionName) {
      const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
      return suggestionName && suggestions && suggestions.length > 0;
    },
    hideModal() {
      this.isClicked = false;
      this.showError = false;
      this.hideAddNewRecordModal(this.modalKey);
    },
    getParentNodes(searchVal, list, field, level = 0, result = {}) {
      if (!list) {
        return {}; //not found
      }
      const primaryField = field.data.options.hierarchyField.primary;


      for (const elem of list) {
        result = JSON.parse(JSON.stringify(result));
        result[level] = elem[primaryField.name];
        if (elem[primaryField.name] === searchVal) {
          return result;
        }

        const foundInChild = this.getParentNodes(searchVal, elem.children, field, level + 1, result);
        if (foundInChild && Object.keys(foundInChild).length > 0) {
          return foundInChild;
        }
      }
    },
    setParentNodes(hierarchyField, searchVal, result = [], level = 0) {
      const primaryField = hierarchyField.data.options.hierarchyField.primary;
      const parent = hierarchyField.data.options.items.find((elem) => searchVal === elem[primaryField.name]);
      if (parent) {
        const jsonData = JSON.parse(hierarchyField.dataTypeJSON);
        if (jsonData.ControlType === 'list') {
          result.push(parent[primaryField.name]);
        } else {
          result.push(parent);
        }

        const parentHierarchyField = hierarchyField.data.options.hierarchyField.parent;
        if (parent[parentHierarchyField.name]) {
          return this.setParentNodes(hierarchyField, parent[parentHierarchyField.name], result, level + 1);
        } else {
          return {...result.reverse()};
        }
      }
    },
    buildTree(arr, hierarchyField) {
      const levelField = hierarchyField.data.options.hierarchyField.level;
      const primaryField = hierarchyField.data.options.hierarchyField.primary;
      const parentField = hierarchyField.data.options.hierarchyField.parent;

      const jsonData = JSON.parse(hierarchyField.dataTypeJSON);
      const minLevel = jsonData.MinimumLevelSelectable;
      const maxLevel = jsonData.MaximumLevelSelectable;

      const tree = [];
      const treeLevels = {};
      const rawArray = JSON.parse(JSON.stringify(arr)); //make copy of original
      const map = {};

      for (const item of rawArray) {
        map[item[primaryField.name]] = item;
      }

      for (const item of rawArray) {
        if (item[parentField.name]) {
          if (!map[item[parentField.name]].children) {
            map[item[parentField.name]].children = [];
          }
          map[item[parentField.name]].children = [...map[item[parentField.name]].children, item];
        } else {
          tree.push(map[item[primaryField.name]]);
        }
      }
      this.setLevelName(tree, 0, levelField, treeLevels, minLevel, maxLevel);

      return {tree, treeLevels};
    },
    setLevelName(arr, level, levelField, treeLevels, minLevel, maxLevel) {
      if (!arr) {
        return;
      }

      let disabled = false;
      if (minLevel > level) {
        if (maxLevel === null) {
          disabled = true;
        }
        if (maxLevel >= level) {
          disabled = true;
        }
      }
      if (maxLevel !== null && !(minLevel === level || maxLevel >= level)) {
        disabled = true;
      }

      arr.forEach((elem) => {
        treeLevels[level] = {
          levelName: elem[levelField.name],
          disabled: disabled,
        };
        elem.level = level;
        elem.$isDisabled = disabled;
        this.setLevelName(elem.children, level + 1, levelField, treeLevels, minLevel, maxLevel);
      });
    },
    async setPrePopulatedForm() {
      const params = {
        entityGuid: this.modal.entityUuid,
        recordGuid: this.modal.recordUuid,
        fieldGuid: this.modal.field.fieldGuid,
      };

      const data = await this.getPopulatedRecord(params);
      const formData = {};
      this.modal.fields.filter((elem) => !elem.isInternal)
        .forEach((field) => {
          if (field.dataTypeName === this.fieldValues.lookup) {
            formData[field.name] = field.data.options.find((el) => el.id === data[field.name]);
          }
          if (data.hasOwnProperty(field.name)) {
            formData[field.name] = data[field.name];
          }
        });
      this.form = {...formData};
    },
    async onShow() {
      this.form = {...this.modal.data};
      this.formFields();
      if (this.modal.defaultLookupValue) {
        this.defaultLookupValue = this.modal.defaultLookupValue;
      }
      if (this.modal.inline) {
        this.inlineLoaded = false;
        await this.getTable({
          id: this.modal.field.entityGuid,
          relationalTableId: this.modal.relationalTableId,
          modalKey: this.modalKey,
          page: 1,
          isModal: true,
          isTodo: false,
        });
        await this.setPrePopulatedForm();
        this.formFields();
        this.inlineLoaded = true;
      }

      this.errors = {};

      this.prepareEventListeners();
      // setTimeout(() => {
      //   if (this.formFields.length > 0) {
      //     const firstInput = this.formFields[0];
      //     const uuid = firstInput.uuid;
      //     if (this.$refs[uuid][0].$refs.currentInput) {
      //       this.$refs[uuid][0].$refs.currentInput.focus();
      //     }
      //   }
      // })

    },
    prepareEventListeners() {
      this.$nextTick(() => {
        const checkboxContainers = document.getElementsByClassName('bulk-update-modal-checkbox');

        for (const checkboxContainer of checkboxContainers) {
          checkboxContainer.addEventListener('click', () => {
            const checkbox = checkboxContainer.getElementsByClassName('custom-control-input')[0];
            const fieldGuid = checkbox.getAttribute('data-field-guid');

            if (fieldGuid) {
              this.currentFields = this.currentFields.map(field => {
                if (field.fieldGuid === fieldGuid && field.disabled) {
                  field.disabled = false;
                }
                return field;
              });
            }
          });
        }
      });
    },
    async onChangedRelationalRow(result) {
      if (result.insertData.ResultSetTemp.length > 0) {
        const modal = this.modal || this.relationalModals[this.modalKey];
        const newItem = result.insertData.updatedRecord;

        const {body} = await axiosClient.get(`api/field/${JSON.parse(modal.field.dataTypeJSON).FieldToDisplayGuid}`);   
        const newOption = {
          id: result.updatedId,
          text: newItem[body.name],
        };

        const payload = {
          parentKey: modal.parentKey,
          fieldName: modal.field.name,
          parentData: modal.parentData,
          newOption,
        };

        await this.updateLookupOptions(payload);
      }
    },
    computedColor(percentage, hue0 = 0, hue1 = 153) {
      const hue = (percentage * (hue1 - hue0)) + hue0;
      return percentage < 0.8 ? 'hsl(' + hue + ', 90%, 80%)' : percentage < 0.9 ? 'hsl(' + hue + ', 48%, 60%)' : 'hsl(' + hue + ', 50%, 50%)';
    },
    isDate(date) {
      return ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'].some(format => moment(date, format).isValid());
    },
    async onInputChange(e, triggered = false) {

      // if (typeof e === 'string' && !this.isDate(e)) return false;
      //
      // let fields = this.modal.fields || this.fields;
      // let businnessKeyFieldExist = fields.some(field => field.isBusinessKey);
      // const validationRulesJson = JSON.parse(this.tableData.validationRulesJson);
      //
      // return;
      // if (validationRulesJson.ValidationRules && validationRulesJson.ValidationRules.length || businnessKeyFieldExist) {
      //   let item = {...this.form};
      //   let postData = this.formatPostData(this.form);
      //
      //   this.currentFields.forEach(field => {
      //     if (!field.isInternal && !this.form[field.name]) {
      //       postData[field.name] = null;
      //     }
      //     if (field.dataTypeName === this.fieldValues.boolean && postData[field.name] === null) {
      //       postData[field.name] = false
      //     }
      //     if (field.dataTypeName === this.fieldValues.lookup) {
      //       let lookupName = `${field.name}__$LookupStatus`
      //       if (item[lookupName]) {
      //         if (item[lookupName].options) {
      //           delete item[lookupName].options
      //         }
      //         if (item[field.name] && item[field.name].id === null && item[lookupName].Suggestions) {
      //           item[lookupName].Suggestions = []
      //         }
      //         postData[lookupName] = item[lookupName]
      //       }
      //     }
      //     if ([this.fieldValues.dateTime, this.fieldValues.date].includes(field.dataTypeName) && item[field.name]) {
      //       const dateFormat = this.getDateFormat(field)
      //       const defaultFormat = 'YYYY/MM/DD'
      //
      //       postData[field.name] = this.fieldValues.date === field.dataTypeName ?
      //           this.$options.filters.toDate(moment(item[field.name], dateFormat).toDate(), defaultFormat) :
      //           this.$options.filters.toDatetime(moment(item[field.name], dateFormat).toDate(), `${defaultFormat} HH:mm:ss`)
      //
      //       if (postData[field.name] === 'Invalid date') {
      //         postData[field.name] = null
      //       }
      //     }
      //     if (field.dataTypeName === this.fieldValues.text && item[field.name]) {
      //       postData[field.name] = item[field.name].trim().replace(/\s\s+/g, ' ')
      //     }
      //   });
      //
      //   postData['entityGuid'] = this.$route.params.tableId;
      //
      //   // If field is hidden make this field value null
      //   const schemaCopy = JSON.parse(JSON.stringify(this.schema));
      //   let currentFields = this.modal.inline ? this.modal.fields : schemaCopy.fields;
      //   currentFields.filter(f => !f.displayInForm).forEach(f => {
      //     if (postData[f.name] === undefined) {
      //       return postData[f.name] = null
      //     }
      //   });
      //
      //   const params = {
      //     data: [postData],
      //     tableId: this.$route.params.tableId,
      //     validateOnly: true,
      //     modalName: 'import'
      //   };
      //
      //   let jsonString = obj => JSON.stringify(obj);
      //
      //   if (!triggered && !(jsonString(this.validationRequest.payload) === jsonString(postData))) this.validationRequest.queue.push(params);
      //
      //   this.validationRequest.payload = postData;
      //
      //   if (this.validationRequest.running || !this.validationRequest.queue.length) return false;
      //
      //   const requestParams = this.validationRequest.queue.shift();
      //
      //   this.validationLoading = true;
      //   this.validationRequest.running = true;
      //   const {success, body} = await this.addNewRecord(requestParams);
      //   this.validationRequest.running = false;
      //
      //   let isValid = true;
      //   if (success) {
      //     body.resultSetTemp.forEach(result => {
      //       let errors = result.validationErrors
      //       if (this.isBulkEditModal) {
      //         errors = result.validationErrors.filter(error => error.indexOf('Failed validation rule') > -1);
      //       }
      //       if (result.uploadStatus === 'rejected' && errors && errors.length) {
      //         // const validationRuleErrorIndex = errors.findIndex(error => error.indexOf('Failed validation rule') > -1);
      //         const businnessKeyValidationRuleErrorIndex = errors.findIndex(error => error.indexOf('Duplicate business key') > -1);
      //         this.validationErrors = this.showError = true;
      //         this.modalError = errors.join('<br>');
      //         isValid = false;
      //         if (businnessKeyValidationRuleErrorIndex > -1) {
      //           this.modalError = errors[businnessKeyValidationRuleErrorIndex];
      //         }
      //       }
      //     });
      //
      //     if(isValid && this.validationErrors) {
      //       this.validationErrors = this.showError = false;
      //       this.modalError = null;
      //     }
      //   }
      //   this.validationLoading = false;
      //   if (this.validationRequest.queue.length) await this.onInputChange(e, true);
      // }
    },
    isCalculatedLookup(field) {
      if (field.dataTypeName === this.fieldValues.lookup) {
        let dataTypeJSON;

        if (field.dataTypeJSON) dataTypeJSON = JSON.parse(field.dataTypeJSON);

        return dataTypeJSON.IsCalculated;
      }
    },
    onLookupChange(selectedOption, propsValue, index, item, field){
      this.form[field.name] = selectedOption.id;
    },
  },
};
</script>

<style lang="scss">
#dmCreateEditModal {
  /deep/ .lobi-spinner {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 1;
  }

  .multiselect__option, .multiselect__single, .multiselect__option span {
    font-size: 13px;
    color: black;
  }

  .multiselect__input, .multiselect__input::placeholder, .multiselect__tags::placeholder {
    font-size: 13px;
  }

  .multiselect, .multiselect__tags, .multiselect__select, .multiselect__option, .multiselect__option::after {
    min-height: 32px;
    height: 32px;
  }

  .multiselect__option {
    padding: 8px;
  }

  .multiselect__tags {
    padding: 0 40px 0 8px;
  }

  .multiselect__select {
    height: 32px;
  }

}


.multiselect__content-wrapper {
  box-shadow: 0 0 10px #c5c4c4;
}

.multiselect__spinner {
  width: 25px !important;
  height: 25px !important;
  right: 5px !important;
  top: 3px !important;
}
.modal-xl{
  min-height: 70vh!important;
}
</style>
